/**
 * TableDataMapper - Maps widgetType strings to their data extraction functions.
 * Used by TableFiltersConfiguration to extract parsed row data for autocomplete suggestions.
 */

import {
  extractInactiveADUsersData,
  extractInactiveADComputersData,
  extractADGroupsRiskData,
  extractADOUsRiskData,
  extractADFSMORolesData,
  extractADGPORisksData,
  extractBackupStatusData,
  extractFirewallStatusData,
  extractOSVersionsData,
  extractSensitiveOpenPortsData,
  extractEncryptionStatusData,
  extractAdminsNoExpiringPasswordData,
  extractAdminsExpiringPasswordData,
  extractAdminsDailyWorkData,
  extractUsersNoExpiringPasswordData,
  extractUsersExpiringPasswordData,
  extractDetailedServicesData,
  extractServicesRiskBreakdownData,
  extractHighPrivilegeServicesData,
  extractAntivirusStatusData
} from './ProofTablesWidgets';

import {
  extractLocalhostFirewallStatusData,
  extractLocalhostOSVersionData,
  extractLocalhostSensitiveOpenPortsData,
  extractLocalhostServicesRiskBreakdownData,
  extractLocalhostHighPrivilegeServicesData,
  extractLocalhostNonDefaultServicesData,
  extractLocalhostOpenPortsData,
  extractLocalhostBrowserPasswordsData,
  extractInactiveMS365UsersData,
  extractUsersWithoutMFAData,
  extractPolicyConfigurationData,
  extractPIIDetectedData,
  extractMS365ScanProofData,
  extractMicrosoftPoliciesData,
  extractLocalhostHardwareInformationData,
  extractLocalhostSystemInformationData,
  extractLocalhostInstalledApplicationsData,
  extractLocalhostEventLogsData,
  extractLocalhostOpenPortsAuditData,
  extractMS365AllUsersMFAData,
  extractADUsersAuditData,
  extractADComputersAuditData,
  extractADDevicesAuditData,
  extractADGroupsAuditData,
  extractADOUsAuditData,
  extractADDomainControllersAuditData,
  extractADGPOPoliciesAuditData,
  extractADEventLogsAuditData,
  extractNetworkLogicalDisksData,
  extractADWorkstationAgeData
} from './ProofTablesWidgets_Part2';
import {
  extractSoftwareInventoryData
} from './SoftwareInventoryWidget';

import {
  extractLocalAdministratorsData
} from './LocalAdministratorsWidget';

import {
  extractADAdminsData
} from './ADAdminsTableWidget';

/**
 * Registry mapping each widgetType to its extraction function.
 * Keys MUST match the widgetType strings used by the widgets (from TABLE_WIDGET_COLUMNS in DataTab.jsx).
 */
export const TableDataMapper = {
  // AD Tables
  'ad-inactive-users-table': { extractor: extractInactiveADUsersData },
  'ad-inactive-computers-table': { extractor: extractInactiveADComputersData },
  'ad-groups-risk-table': { extractor: extractADGroupsRiskData },
  'ad-ous-risk-table': { extractor: extractADOUsRiskData },
  'ad-fsmo-roles-table': { extractor: extractADFSMORolesData },
  'ad-gpo-analysis-table': { extractor: extractADGPORisksData },
  'ad-users-audit-table': { extractor: extractADUsersAuditData },
  'ad-all-computers-table': { extractor: extractADComputersAuditData },
  'ad-computers-audit-table': { extractor: extractADComputersAuditData },
  'ad-devices-audit-table': { extractor: extractADDevicesAuditData },
  'ad-groups-audit-table': { extractor: extractADGroupsAuditData },
  'ad-ous-audit-table': { extractor: extractADOUsAuditData },
  'ad-domain-controllers-audit-table': { extractor: extractADDomainControllersAuditData },
  'ad-gpo-policies-audit-table': { extractor: extractADGPOPoliciesAuditData },
  'ad-event-logs-audit-table': { extractor: extractADEventLogsAuditData },
  'ad-workstation-age-table': { extractor: extractADWorkstationAgeData, availableColumns: ['no', 'hostname', 'lastLogon', 'addedToDomain', 'ageMonths', 'status'] },

  // Network Tables
  'network-backup-status-table': { extractor: extractBackupStatusData },
  'network-firewall-status-table': { extractor: extractFirewallStatusData },
  'network-os-versions-table': { 
    extractor: extractOSVersionsData, 
    availableColumns: ['device', 'hostname', 'os_name', 'last_user_login', 'status', 'count', 'devices'] 
  },
  'network-sensitive-open-ports-table': { extractor: extractSensitiveOpenPortsData },
  'network-encryption-status-table': { extractor: extractEncryptionStatusData },
  'network-admins-no-expiring-password-table': { extractor: extractAdminsNoExpiringPasswordData },
  'network-admins-expiring-password-table': { extractor: extractAdminsExpiringPasswordData },
  'network-admins-daily-work-table': { extractor: extractAdminsDailyWorkData },
  'network-users-no-expiring-password-table': { extractor: extractUsersNoExpiringPasswordData },
  'network-users-expiring-password-table': { extractor: extractUsersExpiringPasswordData },
  'network-detailed-services-table': { extractor: extractDetailedServicesData },
  'network-services-risk-breakdown-table': { extractor: extractServicesRiskBreakdownData },
  'network-high-privilege-services-table': { extractor: extractHighPrivilegeServicesData },
  'network-antivirus-status-table': { extractor: extractAntivirusStatusData, availableColumns: ['No.', 'IP Address', 'Hostname', 'Antivirus', 'Path', 'Status'] },
  'network-logical-disks-table': { extractor: extractNetworkLogicalDisksData, availableColumns: ['No.', 'Device', 'Computer Name', 'Drive Name', 'File System', 'Size (GB)', 'Used (GB)', 'Free (GB)', 'Disk Usage %'] },

  // Localhost Tables
  'localhost-firewall-status-table': { extractor: extractLocalhostFirewallStatusData },
  'localhost-os-version-table': { 
    extractor: extractLocalhostOSVersionData, 
    availableColumns: ['device', 'hostname', 'os_name', 'status'] 
  },
  'localhost-sensitive-open-ports-table': { extractor: extractLocalhostSensitiveOpenPortsData },
  'localhost-services-risk-breakdown-table': { extractor: extractLocalhostServicesRiskBreakdownData },
  'localhost-high-privilege-services-table': { extractor: extractLocalhostHighPrivilegeServicesData },
  'localhost-non-default-services-table': { extractor: extractLocalhostNonDefaultServicesData },
  'localhost-open-ports-table': { extractor: extractLocalhostOpenPortsData },
  'localhost-browser-passwords-table': { extractor: extractLocalhostBrowserPasswordsData },
  'localhost-hardware-information-table': { extractor: extractLocalhostHardwareInformationData },
  'localhost-system-information-table': { extractor: extractLocalhostSystemInformationData },
  'localhost-installed-applications-table': { extractor: extractLocalhostInstalledApplicationsData },
  'localhost-event-logs-table': { extractor: extractLocalhostEventLogsData },
  'localhost-open-ports-audit-table': { extractor: extractLocalhostOpenPortsAuditData },
  'localhost-os-versions-table': { 
    extractor: extractLocalhostOSVersionData, 
    availableColumns: ['device', 'hostname', 'os_name', 'status'] 
  }, // Alias

  // MS365 Tables
  'ms365-inactive-users-table': { extractor: extractInactiveMS365UsersData },
  'ms365-users-without-mfa-table': { extractor: extractUsersWithoutMFAData },
  'ms365-all-users-mfa-table': { extractor: extractMS365AllUsersMFAData },
  'ms365-policy-config-table': { extractor: extractPolicyConfigurationData },
  'ms365-scan-proof-table': { extractor: extractMS365ScanProofData },
  'microsoft-policies-table': { extractor: extractMicrosoftPoliciesData },

  // PII
  'pii-detected-table': { extractor: extractPIIDetectedData },

  // Software Inventory
  'software-inventory-table': { extractor: extractSoftwareInventoryData },

  // Local Administrators
  'local-administrators-table': { extractor: extractLocalAdministratorsData },

  // AD Admins Table
  'ad-admins-table': { extractor: extractADAdminsData }
};

// Add utility functions to the mapper object for easier usage
TableDataMapper.hasWidgetType = function(widgetType) {
  return !!this[widgetType];
};

TableDataMapper.extractData = function(widgetType, rawData) {
  const mapper = this[widgetType];
  if (mapper && typeof mapper.extractor === 'function') {
    return mapper.extractor(rawData);
  }
  return [];
};

export const getExtractDataObj = (widgetType) => {
  return TableDataMapper[widgetType] || null;
};
